/**
 * Days-in-stage + SLA urgency — ONE anchor chain, ONE threshold table (#1156).
 *
 * Four surfaces carried private copies and disagreed: the Kanban put
 * Resourcing on a 14-day red line, the list on 60, the detail page had no
 * band for it at all while its sidebar said "60 days" — so a demand 20 days
 * into Resourcing was red, grey and neutral depending on which tab was open.
 * The table below is the deliberate one (issue #708's, with the new-model
 * stages mirroring their legacy counterparts); everything renders from it.
 */

import type { Demand, DemandStage } from '../types/demand'

/** Age anchor: when the stage was entered, else submission, else creation. */
export function daysInStage(
  d: Pick<Demand, 'stage_entered_at' | 'submission_date' | 'created_at'>,
): number {
  const iso = d.stage_entered_at ?? d.submission_date ?? d.created_at
  if (!iso) return 0
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return 0
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000))
}

export const STAGE_SLA: Record<DemandStage, { amber: number; red: number }> = {
  new:                 { amber: 3, red: 7 }, // untriaged — nudge to start review
  scoping:             { amber: 10, red: 14 },
  triaging:            { amber: 10, red: 14 },
  resourcing:          { amber: 30, red: 60 },
  parking:             { amber: 30, red: 90 }, // parked — keep loose
  closed:              { amber: Number.POSITIVE_INFINITY, red: Number.POSITIVE_INFINITY },
  // Legacy vocabulary — read-tolerance for not-yet-normalized rows; mirrors
  // the canonical stage each aliases to (models/demand.py STAGE_ALIASES).
  under_review:        { amber: 10, red: 14 },
  open_to_application: { amber: 30, red: 60 },
  staffed:             { amber: 30, red: 60 },
  not_prioritized:     { amber: 30, red: 90 },
  resolved:            { amber: Number.POSITIVE_INFINITY, red: Number.POSITIVE_INFINITY },
}

export type SlaTone = 'green' | 'amber' | 'red'

export function slaTone(stage: DemandStage | string, days: number): SlaTone {
  const sla = STAGE_SLA[stage as DemandStage]
  if (!sla) return 'green'
  if (days > sla.red) return 'red'
  if (days > sla.amber) return 'amber'
  return 'green'
}

/** "14 days"-style target for the stages where council commits to one —
 * the sidebar subset. Derived from the red threshold so it cannot drift. */
export function slaTargetLabel(stage: DemandStage | string): string | null {
  if (stage !== 'scoping' && stage !== 'triaging' && stage !== 'resourcing') return null
  const red = STAGE_SLA[stage as DemandStage]?.red
  return Number.isFinite(red) ? `${red} days` : null
}

/** Days before the target lapses — negative once it has. Null off the same subset. */
export function slaDaysLeft(stage: DemandStage | string, days: number): number | null {
  if (slaTargetLabel(stage) === null) return null
  return STAGE_SLA[stage as DemandStage].red - days
}
